"use client";

import { useQuery } from "@tanstack/react-query";
import { useBackend } from "./useBackend";
import { config } from "@/config/env";
import { useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useMemo, useCallback } from "react";

/**
 * Instagram API helper — calls instagram endpoints directly
 * until the Encore generated client includes the instagram service.
 * After running `encore gen client`, this can be replaced with backend.instagram.*
 */
function useInstagramApi() {
  const { getToken } = useAuth();
  const { i18n } = useTranslation();

  const apiCall = useCallback(
    async <T>(method: string, path: string, body?: unknown): Promise<T> => {
      const token = await getToken();
      const headers: Record<string, string> = {
        "Accept-Language": i18n.language,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (body) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(`${config.apiUrl}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Request failed" }));
        const err = new Error(error.message || `HTTP ${response.status}`) as Error & { code?: string };
        err.code = error.code;
        throw err;
      }

      return response.json() as Promise<T>;
    },
    [getToken, i18n.language]
  );

  return apiCall;
}

// --- Types matching backend ---

export interface InstagramAccount {
  id: string;
  userId: string;
  instagramUserId: string;
  instagramUsername: string;
  pageId: string;
  pageName?: string;
  profilePictureUrl?: string;
  connectedAt: string;
  updatedAt: string;
}

export interface InstagramPost {
  id: string;
  userId: string;
  jobId: string;
  instagramAccountId: string;
  containerId?: string;
  mediaId?: string;
  postType: "reel" | "feed";
  caption: string;
  hashtags: string[];
  status: "pending" | "uploading" | "publishing" | "published" | "scheduled" | "failed";
  errorMessage?: string;
  scheduledAt?: string;
  publishedAt?: string;
  instagramPermalink?: string;
  mirrorToFacebook: boolean;
  fbPostType?: "reel" | "video";
  fbMediaId?: string;
  fbPermalink?: string;
  fbStatus?: "pending" | "uploading" | "publishing" | "published" | "failed" | "skipped";
  fbErrorMessage?: string;
  createdAt: string;
}

/**
 * Fetch connected Instagram accounts for the current user.
 */
export function useInstagramAccounts() {
  const apiCall = useInstagramApi();

  return useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: () =>
      apiCall<{ accounts: InstagramAccount[] }>("GET", "/instagram/accounts"),
    staleTime: 30_000,
  });
}

/**
 * Fetch Instagram posts for a specific video job with smart polling.
 * Polls every 3s while any post is still processing.
 */
export function useInstagramPosts(jobId: string | undefined) {
  const apiCall = useInstagramApi();

  return useQuery({
    queryKey: ["instagram-posts", jobId],
    queryFn: () =>
      apiCall<{ posts: InstagramPost[] }>("GET", `/instagram/posts/by-job/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const posts = query.state.data?.posts;
      if (!posts) return false;
      const hasActive = posts.some(
        (p) => p.status === "pending" || p.status === "uploading" || p.status === "publishing"
      );
      return hasActive ? 3000 : false;
    },
  });
}

/**
 * Get the Instagram OAuth URL.
 */
export function useInstagramAuthUrl() {
  const apiCall = useInstagramApi();

  return useCallback(
    () => apiCall<{ authUrl: string }>("GET", "/instagram/auth/url"),
    [apiCall]
  );
}

/**
 * Instagram API mutations (non-hooks, for use in callbacks).
 */
export function useInstagramActions() {
  const apiCall = useInstagramApi();

  return useMemo(
    () => ({
      getAuthUrl: () =>
        apiCall<{ authUrl: string }>("GET", "/instagram/auth/url"),

      handleCallback: (code: string, state: string) =>
        apiCall<{ success: boolean; accountId: string; instagramUsername: string }>(
          "POST",
          "/instagram/auth/callback",
          { code, state }
        ),

      disconnect: (accountId: string) =>
        apiCall<{ success: boolean }>("DELETE", `/instagram/accounts/${accountId}`),

      publish: (params: {
        jobId: string;
        accountId: string;
        postType: string;
        caption: string;
        hashtags: string[];
        scheduledAt?: string;
        mirrorToFacebook?: boolean;
        fbPostType?: string;
      }) =>
        apiCall<{ postId: string; status: string }>("POST", "/instagram/publish", params),

      getPost: (postId: string) =>
        apiCall<{ post: InstagramPost }>("GET", `/instagram/posts/${postId}`),
    }),
    [apiCall]
  );
}
